import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks, SITE_NAME } from '@/constants/site';

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/category/all?keyword=${encodeURIComponent(value)}` : '/category/all');
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur border-b border-background-200'
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-8 lg:px-12">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2.5"
          aria-label={SITE_NAME}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-film-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-semibold ${
                transparent ? 'text-background-50' : 'text-foreground-950'
              }`}
            >
              {SITE_NAME}
            </span>
            <span
              className={`mt-0.5 hidden text-[10px] tracking-[0.2em] sm:block ${
                transparent ? 'text-background-200' : 'text-foreground-400'
              }`}
            >
              FILM · DRAMA · NEWS
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive(link.path)
                  ? transparent
                    ? 'bg-background-50/20 text-background-50'
                    : 'bg-primary-100 text-primary-700'
                  : transparent
                    ? 'text-background-100 hover:bg-background-50/15'
                    : 'text-foreground-700 hover:bg-background-200 hover:text-foreground-950'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto hidden md:block">
          <form onSubmit={submitSearch} className="relative">
            <i
              className={`ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base ${
                transparent ? 'text-background-200' : 'text-foreground-400'
              }`}
            ></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视、角色或资讯"
              className={`h-10 w-56 rounded-full border pl-9 pr-4 text-sm outline-none transition-all focus:w-64 focus:ring-2 focus:ring-primary-400/40 lg:w-64 lg:focus:w-72 ${
                transparent
                  ? 'border-background-50/40 bg-background-50/15 text-background-50 placeholder:text-background-200'
                  : 'border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400'
              }`}
            />
          </form>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md md:hidden ${
            transparent ? 'text-background-50' : 'text-foreground-900'
          }`}
          aria-label="菜单"
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden animate-fade-in">
          <form onSubmit={submitSearch} className="relative mb-4">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视、角色或资讯"
              className="h-11 w-full rounded-md border border-background-300 bg-background-100 pl-9 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-400/40"
            />
          </form>
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`whitespace-nowrap rounded-md px-3.5 py-3 text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-800 hover:bg-background-200'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}